import * as React from 'react';
import _ from 'lodash';

import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { WidgetLocalizationKeys, LazyWidget, IconURIPath } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { IsNullOrEmpty } from '@Utils/Micro';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { PortKeys, QuestionnaireData, QuestionnaireDataPortValidator } from './QuestionnaireStructs';
// --------------------------------------------------------------------------------
const Questionnaire = React.lazy(() => import('./QuestionnaireAsync'));
const QuestionnaireSettings = React.lazy(() => import('./QuestionnaireAsync').then(module => ({ default: module.QuestionnaireSettings })));
// --------------------------------------------------------------------------------
export class QuestionnaireWidget extends IWidgetThingsClass<QuestionnaireData> {
    // --------------------------------------------------------------------------------
    Render(): React.ReactNode {
        return (
            <LazyWidget>
                <Questionnaire />
            </LazyWidget>
        );
    }
    // --------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactNode {
        return (
            <React.Suspense fallback={<LazyLoadingAnimation />}>
                <QuestionnaireSettings
                    key={'settings-' + key}
                    NodeKey={this.NodeKey}
                    ThingKey={this.ThingKey}
                    width={width}
                    height={height}
                    WidgetData={_.cloneDeep(this.WidgetData)}
                    WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                    onWidgetDataUpdate={data => this.WidgetDataUpdate(data)}
                    SettingListToolbar={this.Parent?.SettingsListToolbar} />
            </React.Suspense>
        );
    }
    // --------------------------------------------------------------------------------
    get ports(): WebThingPort[] {
        return [
            new WebThingPort({
                Name: 'Data',
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.Data, data)),
            new WebThingPort({
                Name: 'Save Form',
                PortKey: PortKeys.SaveForm,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Save Results',
                PortKey: PortKeys.SaveResults,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Exit',
                PortKey: PortKeys.Exit,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Delete Form',
                PortKey: PortKeys.DeleteForm,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Toolbar Button Clicked',
                PortKey: PortKeys.ToolbarButtonClicked,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Form Button Clicked',
                PortKey: PortKeys.FormButtonClicked,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger)
        ] as WebThingPort[];
    }
    // --------------------------------------------------------------------------------
    initThing() {
        let webThing = new WebThing(
            this.WebModule,
            WidgetDescriptionToPlegmaThing(this.Description),
            this.ports
        );
        this.ThingKey = webThing.ThingKey;
    }
    // --------------------------------------------------------------------------------
    updateThing() {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        let thing = node.GetThing(this.ThingKey);

        thing.Ports = {};
        _.forEach(this.ports, p => thing.AddPort(p));

        node.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // --------------------------------------------------------------------------------
    GetDefaultWidgetData(): QuestionnaireData {
        return QuestionnaireWidget.GetDefaultWidgetDataStatic();
    }
    // --------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): QuestionnaireData {
        return {
            editable: false,
            toolbarButtons: []
        };
    }
    // --------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            MinW: 1,
            MinH: 1,
            Key: 'QuestionnaireWidget',
            Type: 'QuestionnaireWidget',
            Title: 'Questionnaire',
            Description: '',
            Data: QuestionnaireWidget.GetDefaultWidgetDataStatic(),
            IconURI: IconURIPath + '/svg/tile.svg'
        };
    }
    // --------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        switch (key) {
            case PortKeys.Data:
                return QuestionnaireDataPortValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // --------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let dict = {};
        let widgetTitle = this.Description.WidgetTitle;

        if (!IsNullOrEmpty(widgetTitle)) {
            dict[WidgetLocalizationKeys.Title] = widgetTitle;
        }
        return dict;
    }
    // --------------------------------------------------------------------------------
}